import React, { useState } from 'react';
import { Search, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import BookingDetailModal from './modals/BookingDetailModal';
import { useAdminData } from './AdminContext';
import { formatDate } from './utils';
import { formatLongDate } from '../../utils/dateFormatter';

const AdminBookingsTab = () => {
  const { bookings } = useAdminData();
  const [bookingsSearch, setBookingsSearch] = useState('');
  const [bookingsFilter, setBookingsFilter] = useState('all');
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = bookingsSearch === '' || 
      booking.customer_name?.toLowerCase().includes(bookingsSearch.toLowerCase()) ||
      booking.vendor_name?.toLowerCase().includes(bookingsSearch.toLowerCase()) ||
      booking.event_type?.toLowerCase().includes(bookingsSearch.toLowerCase());
    const matchesFilter = bookingsFilter === 'all' || booking.status === bookingsFilter;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadgeClass = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'completed_by_vendor') return 'bg-blue-100 text-blue-800';
    if (status === 'final_paid') return 'bg-emerald-100 text-emerald-800';
    if (status === 'deposit_paid') return 'bg-cyan-100 text-cyan-800';
    if (status === 'pending_deposit') return 'bg-yellow-100 text-yellow-800';
    if (status?.includes('cancelled')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Booking Management</CardTitle>
          <CardDescription>View and manage customer bookings</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search bookings..."
                value={bookingsSearch}
                onChange={(e) => setBookingsSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={bookingsFilter} onValueChange={setBookingsFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings</SelectItem>
                <SelectItem value="pending_deposit">Pending Deposit</SelectItem>
                <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                <SelectItem value="final_paid">Final Paid</SelectItem>
                <SelectItem value="completed_by_vendor">Completed by Vendor</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled_by_customer">Cancelled by Customer</SelectItem>
                <SelectItem value="cancelled_by_vendor">Cancelled by Vendor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bookings Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No bookings found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.customer_name}</p>
                          <p className="text-sm text-gray-500">{booking.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.vendor_name}</p>
                          <p className="text-sm text-gray-500">{booking.business_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.event_type}</p>
                          <p className="text-sm text-gray-500">{booking.location}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatLongDate(booking.event_date)}</p>
                          <p className="text-sm text-gray-500">{formatDate(booking.created_at)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeClass(booking.status)}>
                          {booking.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                        {booking.cancelled_by && (
                          <p className="text-xs text-red-600 mt-1">By: {booking.cancelled_by}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">${booking.agreed_price?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-gray-500">
                            Deposit: {booking.deposit_paid ? '✓' : '○'} | Final: {booking.final_paid ? '✓' : '○'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setSelectedBookingDetail(booking)}>
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Booking Details Modal */}
      <BookingDetailModal 
        booking={selectedBookingDetail} 
        onClose={() => setSelectedBookingDetail(null)} 
      />
    </>
  );
};

export default AdminBookingsTab;
